/**
 * 项目克隆工具
 * 
 * 深度分析参考项目，生成分析报告和开发计划
 * 用于创建风格一致的新项目
 * 
 * @module tools/cloneProject
 */

import * as fs from 'fs';
import * as path from 'path';
import { ConsoleLogger } from '../core/types.js';
import { 
    ProjectCloneAnalyzer, 
    DeepAnalysisResult, 
    DevelopmentPlan 
} from '../core/projectCloneAnalyzer.js';

/**
 * 分析参考项目（深度分析）
 * 
 * 这是创建新项目的第一步，深度分析参考项目的：
 * - 技术栈和架构
 * - 工具类和配置文件
 * - 样式系统和 UI 组件
 * - 核心页面结构
 * 
 * @example
 * analyze_reference_project({ 
 *   projectPath: "/path/to/reference/project",
 *   generateDocs: true,
 *   newProjectName: "my-new-project"
 * })
 */
export async function analyzeReferenceProject(args: {
    /** 参考项目路径 */
    projectPath: string;
    /** 是否生成分析文档到目标项目 */
    generateDocs?: boolean;
    /** 新项目名称（用于生成开发计划） */
    newProjectName?: string;
    /** 新项目路径（用于生成文档） */
    newProjectPath?: string;
}): Promise<{
    content: Array<{ type: string; text: string }>;
}> {
    const logger = new ConsoleLogger();

    try {
        // 验证参数
        if (!args.projectPath) {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: '请提供参考项目路径 (projectPath)',
                        usage: 'analyze_reference_project({ projectPath: "/path/to/project" })'
                    }, null, 2)
                }]
            };
        }

        if (!fs.existsSync(args.projectPath)) {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: `参考项目路径不存在: ${args.projectPath}`
                    }, null, 2)
                }]
            };
        }

        logger.log(`🔍 开始深度分析参考项目: ${args.projectPath}`);

        // 创建分析器并执行分析
        const analyzer = new ProjectCloneAnalyzer(logger);
        const analysis = await analyzer.analyzeProject(args.projectPath);

        // 如果提供了新项目名称，生成开发计划
        let developmentPlan: DevelopmentPlan | null = null;
        if (args.newProjectName) {
            developmentPlan = analyzer.generateDevelopmentPlan(analysis, args.newProjectName);
        }

        // 如果需要生成文档
        if (args.generateDocs && args.newProjectPath) {
            await generateAnalysisDocs(args.newProjectPath, analysis, developmentPlan);
            logger.log(`📄 已生成分析文档到: ${args.newProjectPath}/docs/`);
        }

        // 构建响应
        const response: {
            success: boolean;
            message: string;
            projectName: string;
            techStack: typeof analysis.techStack;
            architectureSummary: {
                totalLayers: number;
                reusableLayers: number;
                layers: Array<{ name: string; reusable: boolean; priority: string }>;
            };
            utilitiesSummary: {
                total: number;
                recommended: number;
                byType: Record<string, number>;
            };
            styleSystem: {
                preprocessor: string;
                globalStylesCount: number;
                themeFilesCount: number;
                uiLibrary: string | null;
            };
            corePages: {
                hasLogin: boolean;
                hasMainLayout: boolean;
                hasNavigation: boolean;
                errorPagesCount: number;
            };
            qualityAssessment: typeof analysis.qualityAssessment;
            templateSuggestions: typeof analysis.templateSuggestions;
            skeleton: {
                requiredDirectoriesCount: number;
                coreUtilsCount: number;
                directories: Array<{ path: string; description: string }>;
            };
            developmentPlan?: {
                title: string;
                phases: number;
                totalTasks: number;
                phasesSummary: Array<{ phase: number; name: string; tasksCount: number }>;
            };
            nextSteps?: string[];
            _fullAnalysis?: typeof analysis;
        } = {
            success: true,
            message: '项目分析完成',
            projectName: analysis.projectName,
            techStack: analysis.techStack,
            architectureSummary: {
                totalLayers: analysis.architectureLayers.length,
                reusableLayers: analysis.architectureLayers.filter(l => l.reusable).length,
                layers: analysis.architectureLayers.map(l => ({
                    name: l.name,
                    reusable: l.reusable,
                    priority: l.priority
                }))
            },
            utilitiesSummary: {
                total: analysis.utilities.length,
                recommended: analysis.utilities.filter(u => u.recommended).length,
                byType: groupBy(analysis.utilities, 'type')
            },
            styleSystem: {
                preprocessor: analysis.styleSystem.preprocessor,
                globalStylesCount: analysis.styleSystem.globalStyles.length,
                themeFilesCount: analysis.styleSystem.themeFiles.length,
                uiLibrary: analysis.techStack.uiLibrary
            },
            corePages: {
                hasLogin: !!analysis.corePages.loginPage,
                hasMainLayout: !!analysis.corePages.mainLayout,
                hasNavigation: !!analysis.corePages.navigation,
                errorPagesCount: analysis.corePages.errorPages.length
            },
            qualityAssessment: analysis.qualityAssessment,
            templateSuggestions: analysis.templateSuggestions,
            skeleton: {
                requiredDirectoriesCount: analysis.skeleton.requiredDirectories.length,
                coreUtilsCount: analysis.skeleton.coreUtils.length,
                directories: analysis.skeleton.requiredDirectories.map(d => ({
                    path: d.path,
                    description: d.description
                }))
            }
        };

        // 如果有开发计划，添加摘要
        if (developmentPlan) {
            response.developmentPlan = {
                title: developmentPlan.title,
                phases: developmentPlan.phases.length,
                totalTasks: developmentPlan.phases.reduce((sum, p) => sum + p.tasks.length, 0),
                phasesSummary: developmentPlan.phases.map(p => ({
                    phase: p.phase,
                    name: p.name,
                    tasksCount: p.tasks.length
                }))
            };
        }

        // 添加后续步骤提示
        response.nextSteps = [
            '1. 查看分析结果，确认骨架内容符合预期',
            '2. 如需生成完整分析文档，请指定 newProjectPath',
            '3. 使用 generate_project_skeleton 生成新项目',
            '4. 按照开发计划逐步完成项目搭建'
        ];

        // 添加完整分析数据（供 AI 参考）
        response._fullAnalysis = analysis;

        return {
            content: [{
                type: 'text',
                text: JSON.stringify(response, null, 2)
            }]
        };

    } catch (error) {
        logger.error(`分析失败: ${error}`);
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    error: error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined
                }, null, 2)
            }]
        };
    }
}

/**
 * 生成项目骨架
 * 
 * 基于分析结果，在新项目中生成基础骨架
 * 包括目录结构、工具类、配置文件、样式系统等
 */
export async function generateProjectSkeleton(args: {
    /** 参考项目路径 */
    referenceProjectPath: string;
    /** 新项目路径 */
    newProjectPath: string;
    /** 新项目名称 */
    newProjectName: string;
    /** 是否生成分析文档 */
    generateDocs?: boolean;
    /** 要包含的层级（可选，默认包含所有可复用层级） */
    includeLayers?: string[];
    /** 要排除的文件模式 */
    excludePatterns?: string[];
}): Promise<{
    content: Array<{ type: string; text: string }>;
}> {
    const logger = new ConsoleLogger();

    try {
        // 验证参数
        if (!args.referenceProjectPath || !args.newProjectPath || !args.newProjectName) {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: '请提供必要参数',
                        required: ['referenceProjectPath', 'newProjectPath', 'newProjectName']
                    }, null, 2)
                }]
            };
        }

        if (!fs.existsSync(args.referenceProjectPath)) {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: `参考项目路径不存在: ${args.referenceProjectPath}`
                    }, null, 2)
                }]
            };
        }

        logger.log(`🏗️ 开始生成项目骨架...`);
        logger.log(`📦 参考项目: ${args.referenceProjectPath}`);
        logger.log(`📁 新项目: ${args.newProjectPath}`);

        // 先分析参考项目
        const analyzer = new ProjectCloneAnalyzer(logger);
        const analysis = await analyzer.analyzeProject(args.referenceProjectPath);

        // 生成开发计划
        const developmentPlan = analyzer.generateDevelopmentPlan(analysis, args.newProjectName);

        // 创建新项目目录
        if (!fs.existsSync(args.newProjectPath)) {
            fs.mkdirSync(args.newProjectPath, { recursive: true });
        }

        // 生成文档
        if (args.generateDocs !== false) {
            await generateAnalysisDocs(args.newProjectPath, analysis, developmentPlan);
            logger.log(`📄 已生成分析文档`);
        }

        // 生成骨架指南（告诉用户需要复制哪些文件）
        const skeletonGuide = generateSkeletonGuide(analysis, args);

        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    success: true,
                    message: '项目骨架规划完成',
                    newProjectPath: args.newProjectPath,
                    docsGenerated: args.generateDocs !== false,
                    skeletonGuide,
                    developmentPlan: {
                        phases: developmentPlan.phases.length,
                        totalTasks: developmentPlan.phases.reduce((sum, p) => sum + p.tasks.length, 0)
                    },
                    nextSteps: [
                        `1. 查看 ${args.newProjectPath}/docs/ 目录下的分析文档`,
                        '2. 按照 DEVELOPMENT_PLAN.md 中的步骤逐步执行',
                        '3. 根据 PROJECT_ANALYSIS.md 了解参考项目的架构',
                        '4. 复制必要的文件后，运行项目验证'
                    ]
                }, null, 2)
            }]
        };

    } catch (error) {
        logger.error(`生成失败: ${error}`);
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    error: error instanceof Error ? error.message : String(error)
                }, null, 2)
            }]
        };
    }
}

/**
 * 生成分析文档到目标项目
 */
async function generateAnalysisDocs(
    targetPath: string, 
    analysis: DeepAnalysisResult, 
    plan: DevelopmentPlan | null
): Promise<void> {
    const docsPath = path.join(targetPath, 'docs');
    
    // 创建 docs 目录
    if (!fs.existsSync(docsPath)) {
        fs.mkdirSync(docsPath, { recursive: true });
    }

    // 生成项目分析文档
    const analysisDoc = generateAnalysisMarkdown(analysis);
    fs.writeFileSync(path.join(docsPath, 'PROJECT_ANALYSIS.md'), analysisDoc);

    // 生成开发计划文档
    if (plan) {
        const planDoc = generatePlanMarkdown(plan);
        fs.writeFileSync(path.join(docsPath, 'DEVELOPMENT_PLAN.md'), planDoc);
    }

    // 生成骨架清单
    const skeletonDoc = generateSkeletonMarkdown(analysis);
    fs.writeFileSync(path.join(docsPath, 'SKELETON_CHECKLIST.md'), skeletonDoc);
}

/**
 * 生成分析文档 Markdown
 */
function generateAnalysisMarkdown(analysis: DeepAnalysisResult): string {
    let md = `# ${analysis.projectName} 项目分析报告

> 生成时间: ${analysis.analyzedAt}

## 技术栈

| 项目 | 值 |
|------|-----|
| 框架 | ${analysis.techStack.framework} |
| 语言 | ${analysis.techStack.language} |
| 构建工具 | ${analysis.techStack.buildTool} |
| UI 库 | ${analysis.techStack.uiLibrary || '无'} |
| 状态管理 | ${analysis.techStack.stateManagement || '无'} |

## 架构层级

| 层级 | 描述 | 可复用 | 优先级 |
|------|------|--------|--------|
`;

    for (const layer of analysis.architectureLayers) {
        md += `| ${layer.name} | ${layer.description} | ${layer.reusable ? '✅' : '❌'} | ${layer.priority} |\n`;
    }

    md += `
## 工具类分析

**总计**: ${analysis.utilities.length} 个工具类，其中 ${analysis.utilities.filter(u => u.recommended).length} 个推荐复用

### 推荐复用的工具类

| 路径 | 类型 | 使用次数 |
|------|------|---------|
`;

    for (const util of analysis.utilities.filter(u => u.recommended)) {
        md += `| ${util.path} | ${util.type} | ${util.usageCount} |\n`;
    }

    md += `
## 样式系统

- **预处理器**: ${analysis.styleSystem.preprocessor || 'CSS'}
- **全局样式**: ${analysis.styleSystem.globalStyles.length} 个文件
- **主题文件**: ${analysis.styleSystem.themeFiles.length} 个文件
- **Mixins**: ${analysis.styleSystem.mixins.length} 个文件

### 全局样式文件

${analysis.styleSystem.globalStyles.map(f => `- \`${f}\``).join('\n')}

### 主题/变量文件

${analysis.styleSystem.themeFiles.map(f => `- \`${f}\``).join('\n')}

## 配置系统

- **构建配置**: ${analysis.configSystem.buildConfig?.configFile || '无'}
- **路由配置**: ${analysis.configSystem.routerConfig?.configFile || '无'}
- **HTTP 配置**: ${analysis.configSystem.httpConfig?.configFile || '无'}
- **国际化配置**: ${analysis.configSystem.i18nConfig?.configFile || '无'}

## 核心页面

| 页面 | 路径 |
|------|------|
| 登录页 | ${analysis.corePages.loginPage || '无'} |
| 主布局 | ${analysis.corePages.mainLayout || '无'} |
| 导航组件 | ${analysis.corePages.navigation || '无'} |
| 错误页面 | ${analysis.corePages.errorPages.join(', ') || '无'} |

## 质量评估

**评分**: ${analysis.qualityAssessment.score}/100

### 优点

${analysis.qualityAssessment.strengths.map(s => `- ✅ ${s}`).join('\n')}

### 待改进

${analysis.qualityAssessment.weaknesses.map(w => `- ⚠️ ${w}`).join('\n')}

### 建议

${analysis.qualityAssessment.suggestions.map(s => `- 💡 ${s}`).join('\n')}
`;

    return md;
}

/**
 * 生成开发计划 Markdown
 */
function generatePlanMarkdown(plan: DevelopmentPlan): string {
    let md = `# ${plan.title}

> 生成时间: ${plan.generatedAt}

**参考项目**: ${plan.referenceProject}  
**新项目**: ${plan.newProjectName}

---

`;

    for (const phase of plan.phases) {
        md += `## 阶段 ${phase.phase}: ${phase.name}

${phase.description}

| 任务 | 优先级 | 预估时间 | 相关文件 |
|------|--------|---------|----------|
`;
        for (const task of phase.tasks) {
            const files = task.files?.filter(f => f).join(', ') || '-';
            md += `| ${task.task} | ${task.priority} | ${task.estimatedTime} | ${files} |\n`;
        }
        md += '\n';
    }

    md += `## 注意事项

${plan.notes.map(n => `- ${n}`).join('\n')}
`;

    return md;
}

/**
 * 生成骨架清单 Markdown
 */
function generateSkeletonMarkdown(analysis: DeepAnalysisResult): string {
    let md = `# 项目骨架清单

> 基于 ${analysis.projectName} 分析生成

## 必须复制的目录

`;

    for (const dir of analysis.skeleton.requiredDirectories) {
        md += `### ${dir.path}

- **描述**: ${dir.description}
- **关键文件**:
${dir.files?.slice(0, 5).map(f => `  - \`${f}\``).join('\n') || '  - (无)'}

`;
    }

    md += `## 核心工具类

需要复制以下工具类文件：

| 文件 | 类型 | 说明 |
|------|------|------|
`;

    for (const util of analysis.skeleton.coreUtils) {
        md += `| ${util.path} | ${util.type} | 使用 ${util.usageCount} 次 |\n`;
    }

    md += `
## 样式系统

### 必须复制

${analysis.skeleton.styleSystem.globalStyles.map(f => `- [ ] \`${f}\``).join('\n')}

### 主题文件

${analysis.skeleton.styleSystem.themeFiles.map(f => `- [ ] \`${f}\``).join('\n')}

## 核心页面

- [ ] 登录页: \`${analysis.skeleton.corePages.loginPage || '无'}\`
- [ ] 主布局: \`${analysis.skeleton.corePages.mainLayout || '无'}\`
- [ ] 导航组件: \`${analysis.skeleton.corePages.navigation || '无'}\`

## 清理清单

以下业务文件需要删除或清空：

${analysis.skeleton.filesToClean.map(f => `- [ ] \`${f}\``).join('\n')}
`;

    return md;
}

/**
 * 骨架生成参数接口
 */
interface SkeletonArgs {
    referenceProjectPath: string;
    newProjectPath: string;
    newProjectName: string;
    generateDocs?: boolean;
    includeLayers?: string[];
    excludePatterns?: string[];
}

/**
 * 骨架指南接口
 */
interface SkeletonGuide {
    directories: Array<{
        from: string;
        to: string;
        description: string;
    }>;
    coreUtils: Array<{
        from: string;
        type: string;
        recommended: boolean;
    }>;
    styleFiles: string[];
    configFiles: (string | undefined)[];
    corePages: (string | undefined)[];
}

/**
 * 生成骨架指南
 */
function generateSkeletonGuide(analysis: DeepAnalysisResult, args: SkeletonArgs): SkeletonGuide {
    return {
        directories: analysis.skeleton.requiredDirectories.map(d => ({
            from: path.join(args.referenceProjectPath, 'src', d.path),
            to: path.join(args.newProjectPath, 'src', d.path),
            description: d.description
        })),
        coreUtils: analysis.skeleton.coreUtils.map(u => ({
            from: u.path,
            type: u.type,
            recommended: u.recommended
        })),
        styleFiles: [
            ...analysis.styleSystem.globalStyles,
            ...analysis.styleSystem.themeFiles,
            ...analysis.styleSystem.mixins
        ],
        configFiles: [
            analysis.configSystem.buildConfig?.configFile,
            ...analysis.configSystem.envFiles
        ].filter(Boolean),
        corePages: [
            analysis.corePages.loginPage,
            analysis.corePages.mainLayout,
            analysis.corePages.navigation,
            ...analysis.corePages.errorPages
        ].filter(Boolean)
    };
}

/**
 * 可分组项接口
 */
interface GroupableItem {
    [key: string]: unknown;
}

/**
 * 辅助函数：按属性分组
 */
function groupBy(arr: GroupableItem[], key: string): Record<string, number> {
    return arr.reduce((acc, item) => {
        const k = item[key];
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});
}
